/**
 * IAM User Pool Management Component
 * Tabbed interface for managing App Clients, Groups, and Users within a user pool
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { UniversalSchemaTable } from '../shared/UniversalSchemaTable';
import { UniversalSchemaForm } from '../shared/UniversalSchemaForm';
import { IAM_M2M_OPENAPI } from '../../schemas';
import { ApiService } from '../../services/api';
import { 
  handleApiError, 
  handleApiSuccess 
} from '../../utils/mtls-error-handler';

// Export utility functions
const exportToCSV = (data: any[], filename: string) => {
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => headers.map(header => `"${row[header] || ''}"`).join(','))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportToJSON = (data: any, filename: string) => {
  const jsonContent = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
};

interface AppClient {
  clientId: string;
  clientName: string;
  userPoolId: string;
  creationDate: string;
  lastModifiedDate: string;
  refreshTokenValidity?: number;
  accessTokenValidity?: number;
  idTokenValidity?: number;
  clientSecret?: string;
  explicitAuthFlows?: string[];
  allowedOAuthFlows?: string[];
  allowedOAuthScopes?: string[];
  callbackUrls?: string[];
  logoutUrls?: string[];
  generateSecret?: boolean;
}

interface Group {
  groupName: string;
  description: string;
  precedence?: number;
  creationDate: string;
  lastModifiedDate: string;
  userCount?: number;
}

interface User {
  username: string;
  email: string;
  givenName?: string;
  familyName?: string;
  enabled: boolean;
  status: string;
  groups: string[];
  createdDate: string;
  lastModifiedDate: string;
}

interface UserCredentials {
  username: string;
  email: string;
  temporaryPassword: string;
  appClientId: string;
  appClientName: string;
  clientSecret: string;
  userPoolId: string;
  userPoolName: string;
  createdDate: string;
  temporaryPasswordExpiresAt: string;
}

interface IamUserPoolManagementProps {
  userPoolId: string;
  userPoolName: string;
  userPoolEnvironment: string; // Now required from API
  onViewAppClient: (appClientId: string, appClientName: string) => void;
  onViewUser: (username: string) => void;
  onBack: () => void;
}

const IamUserPoolManagement: React.FC<IamUserPoolManagementProps> = ({
  userPoolId,
  userPoolName,
  userPoolEnvironment, // Environment comes from API
  onViewAppClient,
  onViewUser,
  onBack
}) => {
  const [activeTab, setActiveTab] = useState('app-clients');
  const [appClients, setAppClients] = useState<AppClient[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [showCreateAppClientModal, setShowCreateAppClientModal] = useState(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showEditAppClientModal, setShowEditAppClientModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [showDeleteAppClientModal, setShowDeleteAppClientModal] = useState(false);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showSecretWarningModal, setShowSecretWarningModal] = useState(false);
  const [showClientSecretModal, setShowClientSecretModal] = useState(false);
  const [showGroupSelectionModal, setShowGroupSelectionModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  
  // Modal data
  const [modalUsers, setModalUsers] = useState<User[]>([]);
  const [modalTitle, setModalTitle] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [selectedAppClientForUser, setSelectedAppClientForUser] = useState<AppClient | null>(null);
  const [appClientToDelete, setAppClientToDelete] = useState<AppClient | null>(null);
  const [appClientToEdit, setAppClientToEdit] = useState<AppClient | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);
  const [newUserCredentials, setNewUserCredentials] = useState<UserCredentials | null>(null);
  const [selectedAppClientForSecret, setSelectedAppClientForSecret] = useState<AppClient | null>(null);
  const [secretDisplayTimeLeft, setSecretDisplayTimeLeft] = useState(60);
  const [isSecretVisible, setIsSecretVisible] = useState(false);
  const [showCredentialsExportDropdown, setShowCredentialsExportDropdown] = useState(false);
  const [showClientSecretExportDropdown, setShowClientSecretExportDropdown] = useState(false);
  const [selectedUserForGroups, setSelectedUserForGroups] = useState<User | null>(null);
  const [selectedUserForPasswordReset, setSelectedUserForPasswordReset] = useState<User | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [allGroups, setAllGroups] = useState<Group[]>([]); // All groups for selection modal
  const [allGroupsLoading, setAllGroupsLoading] = useState(false); // Loading state for all groups
  const [groupFilter, setGroupFilter] = useState<'all' | 'selected' | 'available'>('all');
  
  // Pagination state for all tabs
  const [groupsPage, setGroupsPage] = useState(1);
  const [groupsTotal, setGroupsTotal] = useState(0);
  const [groupsHasNext, setGroupsHasNext] = useState(false);
  const [groupsFilter, setGroupsFilter] = useState('');
  const groupsPerPage = 20;

  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersHasNext, setUsersHasNext] = useState(false);
  const [usersFilter, setUsersFilter] = useState('');
  const usersPerPage = 20;

  const [appClientsPage, setAppClientsPage] = useState(1);
  const [appClientsTotal, setAppClientsTotal] = useState(0);
  const [appClientsHasNext, setAppClientsHasNext] = useState(false);
  const [appClientsFilter, setAppClientsFilter] = useState('');
  const appClientsPerPage = 20;

  // Track if component has mounted to prevent duplicate initial loads
  const hasMountedRef = useRef(false);
  const isInitialLoadRef = useRef(true);

  // Debounce hook for search
  const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  };

  // Debounced search values (500ms delay)
  const debouncedGroupsFilter = useDebounce(groupsFilter, 500);
  const debouncedUsersFilter = useDebounce(usersFilter, 500);
  const debouncedAppClientsFilter = useDebounce(appClientsFilter, 500);

  // Memoized API functions to prevent unnecessary re-creation
  const fetchGroups = useCallback(async (page: number = 1, filter: string = '') => {
    try {
      const data = await ApiService.listIamGroups(userPoolId, page, groupsPerPage, filter || undefined);
      
      if (data.pagination) {
        setGroupsTotal(data.pagination.total);
        setGroupsHasNext(data.pagination.hasNext);
      }
      
      return data.groups || [];
    } catch (error) {
      console.error('Error fetching groups:', error);
      throw error;
    }
  }, [userPoolId]);

  const fetchAppClients = useCallback(async (page: number = 1, filter: string = '') => {
    try {
      const data = await ApiService.listIamAppClients(userPoolId, page, appClientsPerPage, filter || undefined);
      
      if (data.pagination) {
        setAppClientsTotal(data.pagination.total);
        setAppClientsHasNext(data.pagination.hasNext);
      }
      
      return data.appClients || [];
    } catch (error) {
      console.error('Error fetching app clients:', error);
      throw error;
    }
  }, [userPoolId]);

  const fetchUsers = useCallback(async (page: number = 1, filter: string = '') => {
    try {
      const data = await ApiService.listIamUsers(userPoolId, page, usersPerPage, filter || undefined);
      
      if (data.pagination) {
        setUsersTotal(data.pagination.total);
        setUsersHasNext(data.pagination.hasNext);
      }
      
      return data.users || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }, [userPoolId]);

  // Memoized data loading functions to prevent unnecessary re-renders
  const loadGroupsData = useCallback(async () => {
    setLoading(true);
    try {
      const groupsData = await fetchGroups(groupsPage, debouncedGroupsFilter);
      setGroups(groupsData);
    } catch (error) {
      console.error('Error loading groups:', error);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [fetchGroups, groupsPage, debouncedGroupsFilter]);

  const loadUsersData = useCallback(async () => {
    setLoading(true);
    try {
      const usersData = await fetchUsers(usersPage, debouncedUsersFilter);
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [fetchUsers, usersPage, debouncedUsersFilter]);

  const loadAppClientsData = useCallback(async () => {
    setLoading(true);
    try {
      const appClientsData = await fetchAppClients(appClientsPage, debouncedAppClientsFilter);
      setAppClients(appClientsData);
    } catch (error) {
      console.error('Error loading app clients:', error);
      setAppClients([]);
    } finally {
      setLoading(false);
    }
  }, [fetchAppClients, appClientsPage, debouncedAppClientsFilter]);

  // Single effect for tab changes and initial load
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      // Initial load - only load default tab data
      if (activeTab === 'app-clients') {
        loadAppClientsData();
      }
      return;
    }

    // Tab switching - load data for new tab
    if (activeTab === 'groups') {
      setGroupsPage(1);
      loadGroupsData();
    } else if (activeTab === 'users') {
      setUsersPage(1);
      loadUsersData();
    } else if (activeTab === 'app-clients') {
      setAppClientsPage(1);
      loadAppClientsData();
    }
  }, [activeTab]); // Remove function dependencies to prevent re-runs

  // Optimized search effects - only trigger when there's an actual search change
  useEffect(() => {
    if (activeTab === 'groups' && hasMountedRef.current && !isInitialLoadRef.current) {
      setGroupsPage(1);
      setLoading(true);
      fetchGroups(1, debouncedGroupsFilter)
        .then(groupsData => setGroups(groupsData))
        .catch(error => {
          console.error('Error loading groups:', error);
          setGroups([]);
        })
        .finally(() => setLoading(false));
    }
  }, [debouncedGroupsFilter]); // Remove activeTab and function dependencies

  useEffect(() => {
    if (activeTab === 'users' && hasMountedRef.current && !isInitialLoadRef.current) {
      setUsersPage(1);
      setLoading(true);
      fetchUsers(1, debouncedUsersFilter)
        .then(usersData => setUsers(usersData))
        .catch(error => {
          console.error('Error loading users:', error);
          setUsers([]);
        })
        .finally(() => setLoading(false));
    }
  }, [debouncedUsersFilter]); // Remove activeTab and function dependencies

  useEffect(() => {
    if (activeTab === 'app-clients' && hasMountedRef.current && !isInitialLoadRef.current) {
      setAppClientsPage(1);
      setLoading(true);
      fetchAppClients(1, debouncedAppClientsFilter)
        .then(appClientsData => setAppClients(appClientsData))
        .catch(error => {
          console.error('Error loading app clients:', error);
          setAppClients([]);
        })
        .finally(() => setLoading(false));
    }
  }, [debouncedAppClientsFilter]); // Remove activeTab and function dependencies

  // Mark initial load as complete after first render
  useEffect(() => {
    const timer = setTimeout(() => {
      isInitialLoadRef.current = false;
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Optimized click outside handler with proper cleanup
  useEffect(() => {
    if (!showCredentialsExportDropdown && !showClientSecretExportDropdown) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      if (showCredentialsExportDropdown && !target.closest('.btn-group')) {
        setShowCredentialsExportDropdown(false);
      }
      
      if (showClientSecretExportDropdown && !target.closest('.btn-group')) {
        setShowClientSecretExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCredentialsExportDropdown, showClientSecretExportDropdown]);

  // Optimized fetch all groups with caching
  const fetchAllGroups = useCallback(async () => {
    if (allGroups.length > 0 && !allGroupsLoading) {
      return allGroups; // Return cached data if available
    }

    setAllGroupsLoading(true);
    try {
      const allGroupsData: Group[] = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore) {
        const data = await ApiService.listIamGroups(userPoolId, page, 100);
        
        if (data.groups && data.groups.length > 0) {
          allGroupsData.push(...data.groups);
          hasMore = data.pagination?.hasNext || false;
          page++;
        } else {
          hasMore = false;
        }
      }
      
      setAllGroups(allGroupsData);
      return allGroupsData;
    } catch (error) {
      console.error('Error fetching all groups:', error);
      setAllGroups([]);
      return [];
    } finally {
      setAllGroupsLoading(false);
    }
  }, [userPoolId, allGroups.length, allGroupsLoading]);

  // Memoized action handlers to prevent unnecessary re-renders
  const handleViewAppClientUsers = useCallback(async (appClient: AppClient) => {
    try {
      setModalLoading(true);
      
      try {
        const response = await ApiService.listIamUsersInGroup(userPoolId, appClient.clientId);
        setModalUsers(response.users || []);
        setModalTitle(`Users for App Client: ${appClient.clientName}`);
      } catch (groupError: any) {
        console.error('Error fetching users for app client group:', groupError);
        
        if (groupError?.error?.includes('GROUP_NOT_FOUND') || groupError?.message?.includes('not found')) {
          setModalUsers([]);
          setModalTitle(`Users for App Client: ${appClient.clientName} (No associated group found)`);
        } else {
          setModalUsers([]);
          setModalTitle(`Users for App Client: ${appClient.clientName} (Error loading)`);
        }
      }
      
      setShowUsersModal(true);
    } catch (error) {
      console.error('Error in handleViewAppClientUsers:', error);
      setModalUsers([]);
      setModalTitle(`Users for App Client: ${appClient.clientName} (Error loading)`);
      setShowUsersModal(true);
    } finally {
      setModalLoading(false);
    }
  }, [userPoolId]);

  const handleViewGroupUsers = useCallback(async (group: Group) => {
    try {
      setModalLoading(true);
      const response = await ApiService.listIamUsersInGroup(userPoolId, group.groupName);
      setModalUsers(response.users || []);
      setModalTitle(`Users in Group: ${group.groupName}`);
      setShowUsersModal(true);
    } catch (error) {
      console.error('Error fetching users for group:', error);
      setModalUsers([]);
      setModalTitle(`Users in Group: ${group.groupName} (Error loading)`);
      setShowUsersModal(true);
    } finally {
      setModalLoading(false);
    }
  }, [userPoolId]);

  // Memoized pagination handlers
  const handleGroupsPageChange = useCallback(async (newPage: number) => {
    setGroupsPage(newPage);
    setLoading(true);
    try {
      const groupsData = await fetchGroups(newPage, debouncedGroupsFilter);
      setGroups(groupsData);
    } catch (error) {
      console.error('Error loading groups page:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchGroups, debouncedGroupsFilter]);

  const handleUsersPageChange = useCallback(async (newPage: number) => {
    setUsersPage(newPage);
    setLoading(true);
    try {
      const usersData = await fetchUsers(newPage, debouncedUsersFilter);
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users page:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchUsers, debouncedUsersFilter]);

  const handleAppClientsPageChange = useCallback(async (newPage: number) => {
    setAppClientsPage(newPage);
    setLoading(true);
    try {
      const appClientsData = await fetchAppClients(newPage, debouncedAppClientsFilter);
      setAppClients(appClientsData);
    } catch (error) {
      console.error('Error loading app clients page:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchAppClients, debouncedAppClientsFilter]);

  // Memoized search handlers
  const handleGroupsSearch = useCallback((searchTerm: string) => {
    setGroupsFilter(searchTerm);
  }, []);

  const handleUsersSearch = useCallback((searchTerm: string) => {
    setUsersFilter(searchTerm);
  }, []);

  const handleAppClientsSearch = useCallback((searchTerm: string) => {
    setAppClientsFilter(searchTerm);
  }, []);

  // Memoized delete handlers
  const handleDeleteAppClientConfirm = useCallback(async () => {
    if (!appClientToDelete) return;
    
    try {
      await ApiService.deleteIamAppClient(userPoolId, appClientToDelete.clientId);
      handleApiSuccess('App client deleted successfully', 'IAM Management');
      setShowDeleteAppClientModal(false);
      setAppClientToDelete(null);
      await loadAppClientsData();
    } catch (error) {
      handleApiError(error, 'IAM Management');
    }
  }, [appClientToDelete, userPoolId, loadAppClientsData]);

  const handleConfirmViewSecret = useCallback(async () => {
    if (!selectedAppClientForSecret) return;
    
    setShowSecretWarningModal(false);
    setShowClientSecretModal(true);
    setSecretDisplayTimeLeft(60);
    setIsSecretVisible(false);
    
    try {
      // Fetch the client secret on-demand
      const response = await ApiService.getIamAppClient(
        userPoolId, 
        selectedAppClientForSecret.clientId, 
        true // includeSecret = true
      );
      
      // Update the selected app client with the fetched secret
      if (response.appClient && response.appClient.clientSecret) {
        setSelectedAppClientForSecret(prev => prev ? {
          ...prev,
          clientSecret: response.appClient.clientSecret
        } : null);
      }
    } catch (error) {
      console.error('Failed to fetch client secret:', error);
      handleApiError(error, 'IAM Management');
      // Close the modal on error
      setShowClientSecretModal(false);
      setSelectedAppClientForSecret(null);
      return;
    }
    
    const timer = setInterval(() => {
      setSecretDisplayTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setShowClientSecretModal(false);
          setSelectedAppClientForSecret(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [selectedAppClientForSecret, userPoolId]);

  // Memoized form handlers
  const handleCreateAppClient = useCallback(async (formData: any) => {
    try {
      await ApiService.createIamAppClient(userPoolId, formData);
      handleApiSuccess('App client created successfully', 'IAM Management');
      setShowCreateAppClientModal(false);
      await loadAppClientsData();
    } catch (error) {
      handleApiError(error, 'IAM Management');
    }
  }, [userPoolId, loadAppClientsData]);

  const handleEditAppClient = useCallback(async (formData: any) => {
    if (!appClientToEdit) return;
    
    try {
      await ApiService.updateIamAppClient(userPoolId, appClientToEdit.clientId, formData);
      handleApiSuccess('App client updated successfully', 'IAM Management');
      setShowEditAppClientModal(false);
      setAppClientToEdit(null);
      await loadAppClientsData();
    } catch (error) {
      handleApiError(error, 'IAM Management');
    }
  }, [userPoolId, appClientToEdit, loadAppClientsData]);

  const handleCreateGroup = useCallback(async (formData: any) => {
    try {
      await ApiService.createIamGroup(userPoolId, formData);
      handleApiSuccess('Group created successfully', 'IAM Management');
      setShowCreateGroupModal(false);
      setGroupsPage(1);
      await loadGroupsData();
    } catch (error) {
      handleApiError(error, 'IAM Management');
    }
  }, [userPoolId, loadGroupsData]);

  const handleCreateUser = useCallback(async (formData: any) => {
    try {
      const createUserData = {
        ...formData,
        groups: selectedGroups.length > 0 ? selectedGroups : undefined
      };
      
      const result = await ApiService.createIamUser(userPoolId, createUserData);
      handleApiSuccess('User created successfully', 'IAM Management');
      
      if (result && result.credentials) {
        const appClient = selectedAppClientForUser || appClients.find(ac => ac.clientId === formData.appClientId);
        
        const credentials: UserCredentials = {
          username: result.credentials.username || formData.username,
          email: result.credentials.email || formData.email,
          temporaryPassword: result.credentials.temporaryPassword,
          appClientId: appClient?.clientId || formData.appClientId,
          appClientName: appClient?.clientName || 'Unknown App Client',
          clientSecret: result.credentials.clientSecret || appClient?.clientSecret || '***hidden***',
          userPoolId: userPoolId,
          userPoolName: userPoolName,
          createdDate: result.credentials.createdDate || new Date().toISOString(),
          temporaryPasswordExpiresAt: result.credentials.temporaryPasswordExpiresAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        };

        setNewUserCredentials(credentials);
        setShowCredentialsModal(true);
      }

      setShowCreateUserModal(false);
      setSelectedAppClientForUser(null);
      setSelectedGroups([]);
      setGroupSearchTerm('');
      
      await loadUsersData();
    } catch (error) {
      handleApiError(error, 'IAM Management');
    }
  }, [userPoolId, selectedGroups, selectedAppClientForUser, appClients, userPoolName, loadUsersData]);

  // Memoized delete and user management handlers
  const handleDeleteGroupConfirm = useCallback(async () => {
    if (!groupToDelete) return;
    
    try {
      await ApiService.deleteIamGroup(userPoolId, groupToDelete.groupName);
      handleApiSuccess('Group deleted successfully', 'IAM Management');
      setShowDeleteGroupModal(false);
      setGroupToDelete(null);
      setGroupsPage(1);
      await loadGroupsData();
    } catch (error) {
      handleApiError(error, 'IAM Management');
    }
  }, [groupToDelete, userPoolId, loadGroupsData]);

  const handleDeleteUserConfirm = useCallback(async () => {
    if (!userToDelete) return;
    
    try {
      await ApiService.deleteIamUser(userPoolId, userToDelete.username);
      handleApiSuccess('User deleted successfully', 'IAM Management');
      setShowDeleteUserModal(false);
      setUserToDelete(null);
      await loadUsersData();
    } catch (error) {
      handleApiError(error, 'IAM Management');
    }
  }, [userToDelete, userPoolId, loadUsersData]);

  const handleSaveUserGroups = useCallback(async () => {
    if (!selectedUserForGroups) return;
    
    try {
      const currentGroups = selectedUserForGroups.groups;
      const newGroups = selectedGroups;
      
      const groupsToAdd = newGroups.filter(group => !currentGroups.includes(group));
      const groupsToRemove = currentGroups.filter(group => !newGroups.includes(group));
      
      for (const group of groupsToAdd) {
        await ApiService.assignIamUserToGroup(userPoolId, selectedUserForGroups.username, group);
      }
      for (const group of groupsToRemove) {
        await ApiService.removeIamUserFromGroup(userPoolId, selectedUserForGroups.username, group);
      }
      
      handleApiSuccess('User group assignments updated successfully', 'IAM Management');
      setShowGroupSelectionModal(false);
      setSelectedUserForGroups(null);
      setSelectedGroups([]);
      await loadUsersData();
    } catch (error) {
      handleApiError(error, 'IAM Management');
    }
  }, [selectedUserForGroups, selectedGroups, userPoolId, loadUsersData]);

  // Reset password handler
  const handleResetPassword = useCallback(async (formData: any) => {
    if (!selectedUserForPasswordReset) return;
    
    console.log('Reset password form data:', formData);
    
    try {
      const requestData = {
        resetPassword: {
          newPassword: formData.newPassword,
          temporaryPassword: formData.temporaryPassword
        }
      };
      
      console.log('Sending reset password request:', requestData);
      
      await ApiService.updateIamUser(userPoolId, selectedUserForPasswordReset.username, requestData);
      
      const passwordType = formData.temporaryPassword ? 'temporary' : 'permanent';
      handleApiSuccess(`${passwordType} password reset successfully for ${selectedUserForPasswordReset.username}`, 'IAM Management');
      
      setShowResetPasswordModal(false);
      setSelectedUserForPasswordReset(null);
    } catch (error) {
      handleApiError(error, 'IAM Management');
    }
  }, [selectedUserForPasswordReset, userPoolId]);

  // Simple handler functions for UI actions
  const handleViewUserDetails = useCallback(async (user: User) => {
    try {
      setModalLoading(true);
      setShowUserDetailsModal(true);
      
      // Fetch complete user details with groups
      const userDetails = await ApiService.getIamUser(userPoolId, user.username);
      setSelectedUser(userDetails);
    } catch (error) {
      console.error('Error fetching user details:', error);
      handleApiError(error, 'Failed to fetch user details');
      // Still show modal with list data as fallback
      setSelectedUser(user);
    } finally {
      setModalLoading(false);
    }
  }, [userPoolId]);

  const handleDeleteUser = useCallback((user: User) => {
    setUserToDelete(user);
    setShowDeleteUserModal(true);
  }, []);

  // Memoized utility functions
  const clearGroupFilters = useCallback(() => {
    setGroupSearchTerm('');
    setGroupFilter('all');
  }, []);

  // Memoized utility functions
  const handleToggleGroup = useCallback((groupName: string) => {
    setSelectedGroups(prev => 
      prev.includes(groupName)
        ? prev.filter(g => g !== groupName)
        : [...prev, groupName]
    );
  }, []);

  const getFilteredGroups = useCallback(() => {
    let filteredGroups = allGroups;

    if (groupSearchTerm.trim()) {
      const searchLower = groupSearchTerm.toLowerCase();
      filteredGroups = filteredGroups.filter(group => 
        group.groupName.toLowerCase().includes(searchLower) ||
        (group.description && group.description.toLowerCase().includes(searchLower))
      );
    }

    if (groupFilter === 'selected') {
      filteredGroups = filteredGroups.filter(group => selectedGroups.includes(group.groupName));
    } else if (groupFilter === 'available') {
      filteredGroups = filteredGroups.filter(group => !selectedGroups.includes(group.groupName));
    }

    return filteredGroups;
  }, [allGroups, groupSearchTerm, groupFilter, selectedGroups]);

  const handleSelectAllFilteredGroups = useCallback(() => {
    const filteredGroups = getFilteredGroups();
    const filteredGroupNames = filteredGroups.map(g => g.groupName);
    const newSelected = [...new Set([...selectedGroups, ...filteredGroupNames])];
    setSelectedGroups(newSelected);
  }, [getFilteredGroups, selectedGroups]);

  const handleDeselectAllFilteredGroups = useCallback(() => {
    const filteredGroups = getFilteredGroups();
    const filteredGroupNames = filteredGroups.map(g => g.groupName);
    setSelectedGroups(prev => prev.filter(g => !filteredGroupNames.includes(g)));
  }, [getFilteredGroups]);

  // Memoized refresh handlers for each tab
  const handleRefreshAppClients = useCallback(async () => {
    setLoading(true);
    try {
      const appClientsData = await fetchAppClients(appClientsPage, debouncedAppClientsFilter);
      setAppClients(appClientsData);
    } catch (error) {
      console.error('Error refreshing app clients:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchAppClients, appClientsPage, debouncedAppClientsFilter]);

  const handleRefreshGroups = useCallback(async () => {
    setLoading(true);
    try {
      const groupsData = await fetchGroups(groupsPage, debouncedGroupsFilter);
      setGroups(groupsData);
    } catch (error) {
      console.error('Error refreshing groups:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchGroups, groupsPage, debouncedGroupsFilter]);

  const handleRefreshUsers = useCallback(async () => {
    setLoading(true);
    try {
      const usersData = await fetchUsers(usersPage, debouncedUsersFilter);
      setUsers(usersData);
    } catch (error) {
      console.error('Error refreshing users:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchUsers, usersPage, debouncedUsersFilter]);
  // Memoized action arrays to prevent unnecessary re-renders
  const appClientActions = useMemo(() => [
    {
      label: 'Edit Details',
      icon: 'fas fa-edit',
      variant: 'info' as const,
      onClick: async (appClient: AppClient) => {
        try {
          // Fetch full app client details including authentication flows
          const fullAppClientData = await ApiService.getIamAppClient(userPoolId, appClient.clientId, false);
          console.log('Full app client data:', fullAppClientData);
          console.log('App client to edit:', fullAppClientData.appClient || appClient);
          setAppClientToEdit(fullAppClientData.appClient || appClient);
          setShowEditAppClientModal(true);
        } catch (error) {
          console.error('Error fetching app client details:', error);
          // Fallback to basic data if detailed fetch fails
          console.log('Fallback app client data:', appClient);
          setAppClientToEdit(appClient);
          setShowEditAppClientModal(true);
        }
      }
    },
    {
      label: 'View Users',
      icon: 'fas fa-users',
      variant: 'info' as const,
      onClick: handleViewAppClientUsers
    },
    {
      label: 'Create User',
      icon: 'fas fa-user-plus',
      variant: 'success' as const,
      onClick: (appClient: AppClient) => {
        setSelectedAppClientForUser(appClient);
        fetchAllGroups(); // Fetch all groups for selection
        setShowCreateUserModal(true);
      }
    },
    {
      label: 'View Secret',
      icon: 'fas fa-key',
      variant: 'warning' as const,
      onClick: (appClient: AppClient) => {
        setSelectedAppClientForSecret(appClient);
        setShowSecretWarningModal(true);
      }
    },
    {
      label: 'Delete',
      icon: 'fas fa-trash',
      variant: 'danger' as const,
      onClick: (appClient: AppClient) => {
        setAppClientToDelete(appClient);
        setShowDeleteAppClientModal(true);
      }
    }
  ], [handleViewAppClientUsers]);

  const groupActions = useMemo(() => [
    {
      label: 'View Users',
      icon: 'fas fa-users',
      variant: 'info' as const,
      onClick: handleViewGroupUsers
    },
    {
      label: 'Delete',
      icon: 'fas fa-trash',
      variant: 'danger' as const,
      onClick: (group: Group) => {
        setGroupToDelete(group);
        setShowDeleteGroupModal(true);
      }
    }
  ], [handleViewGroupUsers]);

  const userActions = useMemo(() => [
    {
      label: 'View Details',
      icon: 'fas fa-eye',
      variant: 'primary' as const,
      onClick: handleViewUserDetails
    },
    {
      label: 'Manage Groups',
      icon: 'fas fa-users',
      variant: 'info' as const,
      onClick: (user: User) => {
        setSelectedUserForGroups(user);
        setSelectedGroups([...user.groups]);
        setGroupSearchTerm('');
        setGroupFilter('all');
        fetchAllGroups();
        setShowGroupSelectionModal(true);
      }
    },
    {
      label: 'Delete',
      icon: 'fas fa-trash',
      variant: 'danger' as const,
      onClick: (user: User) => {
        setUserToDelete(user);
        setShowDeleteUserModal(true);
      }
    }
  ], [fetchAllGroups]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'app-clients':
        return (
          <div>
            {/* Search Input and Refresh Button for App Clients */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="input-group">
                  <div className="input-group-prepend">
                    <span className="input-group-text">
                      <i className="fas fa-search"></i>
                    </span>
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search app clients by name or client ID..."
                    value={appClientsFilter}
                    onChange={(e) => handleAppClientsSearch(e.target.value)}
                  />
                  {appClientsFilter && (
                    <div className="input-group-append">
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => handleAppClientsSearch('')}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 text-right">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleRefreshAppClients}
                  disabled={loading}
                  title="Refresh app clients"
                >
                  <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''} mr-2`}></i>
                  Refresh
                </button>
              </div>
            </div>

            <UniversalSchemaTable
              openApiSpec={IAM_M2M_OPENAPI}
              schemaName="AppClient"
              data={appClients}
              loading={loading}
              customActions={appClientActions}
              customLabels={{
                clientId: 'Client ID',
                clientName: 'Client Name',
                creationDate: 'Created',
                lastModifiedDate: 'Last Modified',
                refreshTokenValidity: 'Refresh Token (days)',
                accessTokenValidity: 'Access Token (hours)'
              }}
              customColumnTypes={{
                creationDate: 'datetime' as const,
                lastModifiedDate: 'datetime' as const
              }}
              emptyMessage="No app clients found"
            />
            
            {/* Pagination Controls for App Clients */}
            {appClientsTotal > appClientsPerPage && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  <small className="text-muted">
                    Showing {((appClientsPage - 1) * appClientsPerPage) + 1} to {Math.min(appClientsPage * appClientsPerPage, appClientsTotal)} of {appClientsTotal} app clients
                  </small>
                </div>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${appClientsPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handleAppClientsPageChange(appClientsPage - 1)}
                        disabled={appClientsPage === 1}
                      >
                        <i className="fas fa-chevron-left"></i>
                      </button>
                    </li>
                    
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, Math.ceil(appClientsTotal / appClientsPerPage)) }, (_, i) => {
                      const pageNum = Math.max(1, appClientsPage - 2) + i;
                      const totalPages = Math.ceil(appClientsTotal / appClientsPerPage);
                      
                      if (pageNum > totalPages) return null;
                      
                      return (
                        <li key={pageNum} className={`page-item ${pageNum === appClientsPage ? 'active' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handleAppClientsPageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}
                    
                    <li className={`page-item ${!appClientsHasNext ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handleAppClientsPageChange(appClientsPage + 1)}
                        disabled={!appClientsHasNext}
                      >
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        );

      case 'groups':
        return (
          <div>
            {/* Search Input and Refresh Button for Groups */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="input-group">
                  <div className="input-group-prepend">
                    <span className="input-group-text">
                      <i className="fas fa-search"></i>
                    </span>
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search groups by name or description..."
                    value={groupsFilter}
                    onChange={(e) => handleGroupsSearch(e.target.value)}
                  />
                  {groupsFilter && (
                    <div className="input-group-append">
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => handleGroupsSearch('')}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 text-right">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleRefreshGroups}
                  disabled={loading}
                  title="Refresh groups"
                >
                  <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''} mr-2`}></i>
                  Refresh
                </button>
              </div>
            </div>

            <UniversalSchemaTable
              openApiSpec={IAM_M2M_OPENAPI}
              schemaName="Group"
              data={groups}
              loading={loading}
              customActions={groupActions}
              customLabels={{
                groupName: 'Group Name',
                description: 'Description',
                precedence: 'Precedence',
                creationDate: 'Created',
                lastModifiedDate: 'Last Modified'
              }}
              customColumnTypes={{
                creationDate: 'datetime' as const,
                lastModifiedDate: 'datetime' as const
              }}
              emptyMessage="No groups found"
            />
            
            {/* Pagination Controls */}
            {groupsTotal > groupsPerPage && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  <small className="text-muted">
                    Showing {((groupsPage - 1) * groupsPerPage) + 1} to {Math.min(groupsPage * groupsPerPage, groupsTotal)} of {groupsTotal} groups
                  </small>
                </div>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${groupsPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handleGroupsPageChange(groupsPage - 1)}
                        disabled={groupsPage === 1}
                      >
                        <i className="fas fa-chevron-left"></i>
                      </button>
                    </li>
                    
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, Math.ceil(groupsTotal / groupsPerPage)) }, (_, i) => {
                      const pageNum = Math.max(1, groupsPage - 2) + i;
                      const totalPages = Math.ceil(groupsTotal / groupsPerPage);
                      
                      if (pageNum > totalPages) return null;
                      
                      return (
                        <li key={pageNum} className={`page-item ${pageNum === groupsPage ? 'active' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handleGroupsPageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}
                    
                    <li className={`page-item ${!groupsHasNext ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handleGroupsPageChange(groupsPage + 1)}
                        disabled={!groupsHasNext}
                      >
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        );

      case 'users':
        return (
          <div>
            {/* Search Input and Refresh Button for Users */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="input-group">
                  <div className="input-group-prepend">
                    <span className="input-group-text">
                      <i className="fas fa-search"></i>
                    </span>
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search users by username or email..."
                    value={usersFilter}
                    onChange={(e) => handleUsersSearch(e.target.value)}
                  />
                  {usersFilter && (
                    <div className="input-group-append">
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => handleUsersSearch('')}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 text-right">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleRefreshUsers}
                  disabled={loading}
                  title="Refresh users"
                >
                  <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''} mr-2`}></i>
                  Refresh
                </button>
              </div>
            </div>

            <UniversalSchemaTable
              openApiSpec={IAM_M2M_OPENAPI}
              schemaName="User"
              data={users}
              loading={loading}
              customActions={userActions}
              customLabels={{
                username: 'Username',
                email: 'Email',
                givenName: 'First Name',
                familyName: 'Last Name',
                enabled: 'Enabled',
                status: 'Status',
                groups: 'Groups',
                createdDate: 'Created',
                lastModifiedDate: 'Last Modified'
              }}
              customColumnTypes={{
                enabled: 'badge' as const,
                status: 'badge' as const,
                createdDate: 'datetime' as const,
                lastModifiedDate: 'datetime' as const
              }}
              emptyMessage="No users found"
            />
            
            {/* Pagination Controls for Users */}
            {usersTotal > usersPerPage && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  <small className="text-muted">
                    Showing {((usersPage - 1) * usersPerPage) + 1} to {Math.min(usersPage * usersPerPage, usersTotal)} of {usersTotal} users
                  </small>
                </div>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${usersPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handleUsersPageChange(usersPage - 1)}
                        disabled={usersPage === 1}
                      >
                        <i className="fas fa-chevron-left"></i>
                      </button>
                    </li>
                    
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, Math.ceil(usersTotal / usersPerPage)) }, (_, i) => {
                      const pageNum = Math.max(1, usersPage - 2) + i;
                      const totalPages = Math.ceil(usersTotal / usersPerPage);
                      
                      if (pageNum > totalPages) return null;
                      
                      return (
                        <li key={pageNum} className={`page-item ${pageNum === usersPage ? 'active' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handleUsersPageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}
                    
                    <li className={`page-item ${!usersHasNext ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handleUsersPageChange(usersPage + 1)}
                        disabled={!usersHasNext}
                      >
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        );

      default:
        return <div>Unknown tab</div>;
    }
  };

  return (
    <div className="row">
      <div className="col-12">
        {/* Development Mode Indicator */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">
              <i className="fas fa-cogs mr-2"></i>
              {userPoolName}
            </h3>
            <div className="card-tools">
              <button
                type="button"
                className="btn btn-tool"
                onClick={onBack}
                title="Back to User Pools"
              >
                <i className="fas fa-arrow-left"></i>
              </button>
            </div>
          </div>
          
          <div className="card-header p-0 pt-1 border-bottom-0">
            <ul className="nav nav-tabs" id="user-pool-tabs" role="tablist">
              <li className="nav-item">
                <a
                  className={`nav-link ${activeTab === 'app-clients' ? 'active' : ''}`}
                  onClick={() => setActiveTab('app-clients')}
                  href="#"
                >
                  <i className="fas fa-key mr-2"></i>
                  App Clients
                </a>
              </li>
              <li className="nav-item">
                <a
                  className={`nav-link ${activeTab === 'groups' ? 'active' : ''}`}
                  onClick={() => setActiveTab('groups')}
                  href="#"
                >
                  <i className="fas fa-users mr-2"></i>
                  Groups
                </a>
              </li>
              <li className="nav-item">
                <a
                  className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
                  onClick={() => setActiveTab('users')}
                  href="#"
                >
                  <i className="fas fa-user mr-2"></i>
                  Users
                </a>
              </li>
            </ul>
          </div>

          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-6">
                <h5>
                  {activeTab === 'app-clients' && 'App Clients'}
                  {activeTab === 'groups' && 'Groups'}
                  {activeTab === 'users' && 'Users'}
                </h5>
                <p className="text-muted mb-0">
                  {activeTab === 'app-clients' && 'Manage Cognito App Clients with automatic group creation'}
                  {activeTab === 'groups' && 'Manage user groups and permissions'}
                  {activeTab === 'users' && 'View and manage users. To create new users, use the "Create User" action from the App Clients tab.'}
                </p>
              </div>
              <div className="col-md-6 text-right">
                {activeTab === 'app-clients' && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowCreateAppClientModal(true)}
                  >
                    <i className="fas fa-plus mr-2"></i>
                    Create App Client
                  </button>
                )}
                {activeTab === 'groups' && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowCreateGroupModal(true)}
                  >
                    <i className="fas fa-plus mr-2"></i>
                    Create Group
                  </button>
                )}
                {/* No create button for users tab - users should be created from app clients */}
              </div>
            </div>

            {activeTab === 'app-clients' && (
              <div className="alert alert-info">
                <i className="fas fa-info-circle mr-2"></i>
                <strong>Orchestration:</strong> Creating an app client automatically creates an associated group (GroupName = ClientId). 
                Use the "Create User" action on each app client to create users pre-assigned to that client's group.
              </div>
            )}

            {activeTab === 'users' && (
              <div className="alert alert-info">
                <i className="fas fa-info-circle mr-2"></i>
                <strong>Creating Users:</strong> To create a new user, go to the "App Clients" tab and use the "Create User" action. 
                This ensures the user is properly assigned to the correct app client group.
              </div>
            )}

            {renderTabContent()}
          </div>
        </div>
      </div>

      {/* Create App Client Modal */}
      {showCreateAppClientModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  <i className="fas fa-key mr-2"></i>
                  Create App Client
                </h4>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowCreateAppClientModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle mr-2"></i>
                  Creating an app client will automatically create an associated group with GroupName = ClientId
                </div>
                <UniversalSchemaForm
                  openApiSpec={IAM_M2M_OPENAPI}
                  schemaName="CreateAppClientRequest"
                  onSubmit={handleCreateAppClient}
                  customLabels={{ appClientName: 'App Client Name' }}
                  customPlaceholders={{ appClientName: 'e.g., My-API-Client' }}
                  showSubmitButton={false}
                  formId="create-app-client-form"
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateAppClientModal(false)}
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </button>
                <button
                  type="submit"
                  form="create-app-client-form"
                  className="btn btn-primary"
                >
                  <i className="fas fa-plus mr-2"></i>
                  Create App Client
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit App Client Modal */}
      <Modal show={showEditAppClientModal} onHide={() => {
        setShowEditAppClientModal(false);
        setAppClientToEdit(null);
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-edit mr-2"></i>
            Edit App Client: {appClientToEdit?.clientName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {appClientToEdit && (
            <>
              <div className="alert alert-info">
                <i className="fas fa-info-circle mr-2"></i>
                <strong>Client ID:</strong> {appClientToEdit.clientId}
              </div>
              
              <UniversalSchemaForm
                openApiSpec={IAM_M2M_OPENAPI}
                schemaName="UpdateAppClientRequest"
                onSubmit={handleEditAppClient}
                showSubmitButton={false}
                formId="edit-app-client-form"
                initialData={{
                  clientName: appClientToEdit.clientName,
                  refreshTokenValidity: appClientToEdit.refreshTokenValidity,
                  accessTokenValidity: appClientToEdit.accessTokenValidity,
                  idTokenValidity: appClientToEdit.idTokenValidity,
                  explicitAuthFlows: Array.isArray(appClientToEdit.explicitAuthFlows) ? appClientToEdit.explicitAuthFlows : [],
                  allowedOAuthFlows: Array.isArray(appClientToEdit.allowedOAuthFlows) ? appClientToEdit.allowedOAuthFlows : [],
                  allowedOAuthScopes: Array.isArray(appClientToEdit.allowedOAuthScopes) ? appClientToEdit.allowedOAuthScopes : [],
                  callbackUrls: Array.isArray(appClientToEdit.callbackUrls) ? appClientToEdit.callbackUrls : [],
                  logoutUrls: Array.isArray(appClientToEdit.logoutUrls) ? appClientToEdit.logoutUrls : []
                }}
                customLabels={{
                  clientName: 'App Client Name',
                  refreshTokenValidity: 'Refresh Token Validity (days)',
                  accessTokenValidity: 'Access Token Validity (minutes)',
                  idTokenValidity: 'ID Token Validity (minutes)',
                  explicitAuthFlows: 'Authentication Flows',
                  allowedOAuthFlows: 'OAuth Flows',
                  allowedOAuthScopes: 'OAuth Scopes',
                  callbackUrls: 'Callback URLs',
                  logoutUrls: 'Logout URLs'
                }}
                customPlaceholders={{
                  clientName: 'e.g., My API Client',
                  refreshTokenValidity: 'e.g., 30',
                  accessTokenValidity: 'e.g., 60',
                  idTokenValidity: 'e.g., 60',
                  allowedOAuthScopes: 'e.g., openid, email, profile',
                  callbackUrls: 'Enter one URL per line',
                  logoutUrls: 'Enter one URL per line'
                }}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowEditAppClientModal(false);
              setAppClientToEdit(null);
            }}
          >
            <i className="fas fa-times mr-1"></i>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              // Trigger form submission by finding the form and calling submit
              const form = document.querySelector('#edit-app-client-form');
              if (form) {
                (form as HTMLFormElement).requestSubmit();
              } else {
                console.error('Edit app client form not found with selector #edit-app-client-form');
              }
            }}
          >
            <i className="fas fa-save mr-1"></i>
            Update App Client
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Group Modal */}
      {showCreateGroupModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  <i className="fas fa-users mr-2"></i>
                  Create Group
                </h4>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowCreateGroupModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <UniversalSchemaForm
                  openApiSpec={IAM_M2M_OPENAPI}
                  schemaName="CreateGroupRequest"
                  onSubmit={handleCreateGroup}
                  customLabels={{
                    groupName: 'Group Name',
                    description: 'Description'
                  }}
                  customPlaceholders={{
                    groupName: 'e.g., api-users',
                    description: 'e.g., Users with API access'
                  }}
                  showSubmitButton={false}
                  formId="create-group-form"
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateGroupModal(false)}
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </button>
                <button
                  type="submit"
                  form="create-group-form"
                  className="btn btn-primary"
                >
                  <i className="fas fa-plus mr-2"></i>
                  Create Group
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  <i className="fas fa-user mr-2"></i>
                  Create User
                  {selectedAppClientForUser && (
                    <small className="text-muted ml-2">
                      for {selectedAppClientForUser.clientName}
                    </small>
                  )}
                </h4>
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    setShowCreateUserModal(false);
                    setSelectedAppClientForUser(null);
                    setSelectedGroups([]); // Clear selected groups
                    setGroupSearchTerm(''); // Clear search term
                  }}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {selectedAppClientForUser ? (
                  <div className="alert alert-success">
                    <i className="fas fa-info-circle mr-2"></i>
                    Creating a user for app client <strong>{selectedAppClientForUser.clientName}</strong>. 
                    The user will be automatically assigned to the group <strong>{selectedAppClientForUser.clientId}</strong>.
                  </div>
                ) : (
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle mr-2"></i>
                    Creating a user will automatically assign them to the specified app client's group
                  </div>
                )}
                
                <UniversalSchemaForm
                  openApiSpec={IAM_M2M_OPENAPI}
                  schemaName="CreateUserRequest"
                  onSubmit={handleCreateUser}
                  initialData={selectedAppClientForUser ? {
                    appClientId: selectedAppClientForUser.clientId
                  } : {}}
                  customLabels={{
                    username: 'Username',
                    email: 'Email',
                    password: 'Temporary Password',
                    appClientId: 'App Client ID'
                  }}
                  customPlaceholders={{
                    username: 'e.g., john.doe',
                    email: 'e.g., john.doe@example.com',
                    password: 'Temporary password',
                    appClientId: selectedAppClientForUser 
                      ? `Pre-selected: ${selectedAppClientForUser.clientName}` 
                      : 'Select from app clients above'
                  }}
                  showSubmitButton={false}
                  formId="create-user-form"
                  hiddenFields={selectedAppClientForUser ? ['appClientId', 'groups'] : ['groups']}
                />
                
                {/* Additional Groups Selection */}
                <div className="mt-3">
                  <h6><i className="fas fa-users mr-2"></i>Additional Groups (Optional)</h6>
                  <small className="text-muted">
                    The user will automatically be assigned to the app client group. 
                    You can optionally select additional groups below.
                  </small>
                  
                  {/* Search for groups */}
                  {allGroups.length > 10 && (
                    <div className="mt-2 mb-2">
                      <div className="input-group input-group-sm">
                        <div className="input-group-prepend">
                          <span className="input-group-text">
                            <i className="fas fa-search"></i>
                          </span>
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search groups..."
                          value={groupSearchTerm}
                          onChange={(e) => setGroupSearchTerm(e.target.value)}
                        />
                        {groupSearchTerm && (
                          <div className="input-group-append">
                            <button
                              className="btn btn-outline-secondary"
                              type="button"
                              onClick={() => setGroupSearchTerm('')}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="border rounded p-3 mt-2" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                    {(() => {
                      const availableGroups = allGroups.filter(group => 
                        !selectedAppClientForUser || group.groupName !== selectedAppClientForUser.clientId
                      );
                      
                      const filteredGroups = groupSearchTerm.trim() 
                        ? availableGroups.filter(group => 
                            group.groupName.toLowerCase().includes(groupSearchTerm.toLowerCase()) ||
                            (group.description && group.description.toLowerCase().includes(groupSearchTerm.toLowerCase()))
                          )
                        : availableGroups;
                      
                      if (allGroupsLoading) {
                        return (
                          <div className="text-center py-2">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                              <span className="sr-only">Loading...</span>
                            </div>
                            <small className="text-muted ml-2">Loading groups...</small>
                          </div>
                        );
                      } else if (filteredGroups.length > 0) {
                        return (
                          <>
                            {groupSearchTerm && (
                              <div className="mb-2">
                                <small className="text-muted">
                                  Showing {filteredGroups.length} of {availableGroups.length} groups
                                </small>
                              </div>
                            )}
                            {filteredGroups.map(group => (
                              <div key={group.groupName} className="form-check mb-2">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`create-user-group-${group.groupName}`}
                                  checked={selectedGroups.includes(group.groupName)}
                                  onChange={() => handleToggleGroup(group.groupName)}
                                />
                                <label className="form-check-label" htmlFor={`create-user-group-${group.groupName}`}>
                                  <strong>{group.groupName}</strong>
                                  {group.description && (
                                    <small className="text-muted d-block">{group.description}</small>
                                  )}
                                </label>
                              </div>
                            ))}
                          </>
                        );
                      } else if (groupSearchTerm) {
                        return (
                          <div className="text-center py-2">
                            <small className="text-muted">No groups found matching "{groupSearchTerm}"</small>
                            <br />
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-0"
                              onClick={() => setGroupSearchTerm('')}
                            >
                              Clear search
                            </button>
                          </div>
                        );
                      } else {
                        return (
                          <div className="text-center py-2">
                            <small className="text-muted">No additional groups available</small>
                          </div>
                        );
                      }
                    })()}
                  </div>
                  
                  {selectedGroups.length > 0 && (
                    <div className="mt-2">
                      <small className="text-success">
                        <i className="fas fa-check mr-1"></i>
                        <strong>Selected additional groups ({selectedGroups.length}):</strong> {selectedGroups.join(', ')}
                      </small>
                    </div>
                  )}
                  
                  {allGroups.length > 20 && (
                    <div className="mt-2">
                      <small className="text-info">
                        <i className="fas fa-info-circle mr-1"></i>
                        <strong>Tip:</strong> Use the search box above to quickly find groups from {allGroups.length} available groups.
                      </small>
                    </div>
                  )}
                </div>
                
                {selectedAppClientForUser && (
                  <div className="mt-3">
                    <small className="text-muted">
                      <strong>App Client:</strong> {selectedAppClientForUser.clientName} ({selectedAppClientForUser.clientId})
                    </small>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreateUserModal(false);
                    setSelectedAppClientForUser(null);
                    setSelectedGroups([]);
                    setGroupSearchTerm('');
                  }}
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </button>
                <button
                  type="submit"
                  form="create-user-form"
                  className="btn btn-primary"
                >
                  <i className="fas fa-user-plus mr-2"></i>
                  Create User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users Modal */}
      {showUsersModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  <i className="fas fa-users mr-2"></i>
                  {modalTitle}
                </h4>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowUsersModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {modalLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-2">Loading users...</p>
                  </div>
                ) : modalUsers.length > 0 ? (
                  <UniversalSchemaTable
                    openApiSpec={IAM_M2M_OPENAPI}
                    schemaName="User"
                    data={modalUsers}
                    loading={false}
                    customActions={[
                      {
                        label: 'View Details',
                        icon: 'fas fa-eye',
                        variant: 'primary' as const,
                        onClick: handleViewUserDetails
                      }
                    ]}
                    customLabels={{
                      username: 'Username',
                      email: 'Email',
                      status: 'Status',
                      enabled: 'Enabled',
                      createdDate: 'Created'
                    }}
                    customColumnTypes={{
                      enabled: 'badge' as const,
                      status: 'badge' as const,
                      createdDate: 'datetime' as const
                    }}
                    emptyMessage="No users found"
                  />
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-users fa-3x text-muted mb-3"></i>
                    <h5>No Users Found</h5>
                    <p className="text-muted">No users are currently assigned to this group.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showUserDetailsModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  <i className="fas fa-user mr-2"></i>
                  User Details{selectedUser ? `: ${selectedUser.username}` : ''}
                </h4>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowUserDetailsModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {modalLoading ? (
                  <div className="text-center py-4">
                    <i className="fas fa-spinner fa-spin fa-2x text-primary mb-3"></i>
                    <h5>Loading User Details...</h5>
                    <p className="text-muted">Fetching complete user information including group assignments.</p>
                  </div>
                ) : selectedUser ? (
                  <>
                    {/* User Details Card Layout */}
                    <div className="row">
                      <div className="col-md-6">
                        <div className="card">
                          <div className="card-header">
                            <h6 className="card-title mb-0">
                              <i className="fas fa-user mr-2"></i>
                              User Information
                            </h6>
                          </div>
                          <div className="card-body">
                            <dl className="row mb-0">
                              <dt className="col-sm-4">Username:</dt>
                              <dd className="col-sm-8">{selectedUser.username}</dd>
                              
                              <dt className="col-sm-4">Email:</dt>
                              <dd className="col-sm-8">{selectedUser.email}</dd>
                              
                              {selectedUser.givenName && (
                                <>
                                  <dt className="col-sm-4">First Name:</dt>
                                  <dd className="col-sm-8">{selectedUser.givenName}</dd>
                                </>
                              )}
                              
                              {selectedUser.familyName && (
                                <>
                                  <dt className="col-sm-4">Last Name:</dt>
                                  <dd className="col-sm-8">{selectedUser.familyName}</dd>
                                </>
                              )}
                              
                              <dt className="col-sm-4">Status:</dt>
                              <dd className="col-sm-8">
                                <span className={`badge badge-${selectedUser.enabled ? 'success' : 'danger'}`}>
                                  {selectedUser.status}
                                </span>
                                {!selectedUser.enabled && (
                                  <small className="text-muted ml-2">(Disabled)</small>
                                )}
                              </dd>
                              
                              <dt className="col-sm-4">Created:</dt>
                              <dd className="col-sm-8">
                                {new Date(selectedUser.createdDate).toLocaleString()}
                              </dd>
                              
                              <dt className="col-sm-4">Last Modified:</dt>
                              <dd className="col-sm-8">
                                {new Date(selectedUser.lastModifiedDate).toLocaleString()}
                              </dd>
                            </dl>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div className="card">
                          <div className="card-header">
                            <h6 className="card-title mb-0">
                              <i className="fas fa-users mr-2"></i>
                              Group Memberships
                            </h6>
                          </div>
                          <div className="card-body">
                            {selectedUser.groups && selectedUser.groups.length > 0 ? (
                              <div>
                                <p className="text-muted mb-2">
                                  This user is a member of {selectedUser.groups.length} group{selectedUser.groups.length !== 1 ? 's' : ''}:
                                </p>
                                <div className="d-flex flex-wrap">
                                  {selectedUser.groups.map((group, index) => (
                                    <span key={index} className="badge badge-info mr-2 mb-2 p-2">
                                      <i className="fas fa-users mr-1"></i>
                                      {group}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="text-center py-3">
                                <i className="fas fa-users fa-2x text-muted mb-2"></i>
                                <p className="text-muted mb-0">No groups assigned</p>
                                <small className="text-muted">
                                  Use "Manage Groups" below to assign this user to groups
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <hr />
                    
                    <div className="row">
                      <div className="col-12">
                        <h6>Actions</h6>
                        <div className="btn-group" role="group">
                          <button 
                            type="button" 
                            className="btn btn-warning"
                            onClick={() => {
                              setSelectedUserForPasswordReset(selectedUser);
                              setShowResetPasswordModal(true);
                            }}
                          >
                            <i className="fas fa-key mr-2"></i>
                            Reset Password
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-info"
                            onClick={() => {
                              setShowUserDetailsModal(false);
                              setSelectedUserForGroups(selectedUser);
                              setSelectedGroups([...selectedUser.groups]);
                              setGroupSearchTerm(''); // Clear search term
                              setGroupFilter('all'); // Reset filter
                              fetchAllGroups(); // Fetch all groups for selection
                              setShowGroupSelectionModal(true);
                            }}
                          >
                            <i className="fas fa-users mr-2"></i>
                            Manage Groups
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-danger"
                            onClick={() => {
                              setShowUserDetailsModal(false);
                              handleDeleteUser(selectedUser!);
                            }}
                          >
                            <i className="fas fa-trash mr-2"></i>
                            Delete User
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-exclamation-triangle fa-2x text-warning mb-3"></i>
                    <h5>Error Loading User Details</h5>
                    <p className="text-muted">Failed to load user information. Please try again.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteUserModal && userToDelete && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  <i className="fas fa-exclamation-triangle mr-2 text-warning"></i>
                  Confirm Delete User
                </h4>
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    setShowDeleteUserModal(false);
                    setUserToDelete(null);
                  }}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  <strong>Warning:</strong> This action cannot be undone.
                </div>
                <p>
                  Are you sure you want to delete user <strong>{userToDelete.username}</strong>?
                </p>
                <div className="row">
                  <div className="col-md-6">
                    <small className="text-muted">
                      <strong>Email:</strong> {userToDelete.email}
                    </small>
                  </div>
                  <div className="col-md-6">
                    <small className="text-muted">
                      <strong>Groups:</strong> {userToDelete.groups.length > 0 ? userToDelete.groups.join(', ') : 'None'}
                    </small>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteUserModal(false);
                    setUserToDelete(null);
                  }}
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteUserConfirm}
                >
                  <i className="fas fa-trash mr-2"></i>
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete App Client Confirmation Modal */}
      {showDeleteAppClientModal && appClientToDelete && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  <i className="fas fa-exclamation-triangle mr-2 text-warning"></i>
                  Confirm Delete App Client
                </h4>
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    setShowDeleteAppClientModal(false);
                    setAppClientToDelete(null);
                  }}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  <strong>Warning:</strong> This action cannot be undone and will also delete the associated group.
                </div>
                <p>
                  Are you sure you want to delete app client <strong>{appClientToDelete.clientName}</strong>?
                </p>
                <div className="row">
                  <div className="col-md-6">
                    <small className="text-muted">
                      <strong>Client ID:</strong> {appClientToDelete.clientId}
                    </small>
                  </div>
                  <div className="col-md-6">
                    <small className="text-muted">
                      <strong>Associated Group:</strong> {appClientToDelete.clientId}
                    </small>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteAppClientModal(false);
                    setAppClientToDelete(null);
                  }}
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteAppClientConfirm}
                >
                  <i className="fas fa-trash mr-2"></i>
                  Delete App Client
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Group Confirmation Modal */}
      {showDeleteGroupModal && groupToDelete && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  <i className="fas fa-exclamation-triangle mr-2 text-warning"></i>
                  Confirm Delete Group
                </h4>
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    setShowDeleteGroupModal(false);
                    setGroupToDelete(null);
                  }}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  <strong>Warning:</strong> This action cannot be undone.
                </div>
                <p>
                  Are you sure you want to delete group <strong>{groupToDelete.groupName}</strong>?
                </p>
                <div className="row">
                  <div className="col-md-6">
                    <small className="text-muted">
                      <strong>Description:</strong> {groupToDelete.description || 'No description'}
                    </small>
                  </div>
                  <div className="col-md-6">
                    <small className="text-muted">
                      <strong>Users:</strong> {groupToDelete.userCount || 0} users
                    </small>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteGroupModal(false);
                    setGroupToDelete(null);
                  }}
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteGroupConfirm}
                >
                  <i className="fas fa-trash mr-2"></i>
                  Delete Group
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Credentials Modal */}
      {showCredentialsModal && newUserCredentials && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  <i className="fas fa-user-check mr-2 text-success"></i>
                  User Created Successfully - Complete Credentials
                </h4>
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    setShowCredentialsModal(false);
                    setNewUserCredentials(null);
                  }}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-success">
                  <i className="fas fa-check-circle mr-2"></i>
                  <strong>User created successfully!</strong> Save these credentials securely - they will not be shown again.
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <h5><i className="fas fa-user mr-2"></i>User Credentials</h5>
                    <div className="form-group">
                      <label><strong>Username</strong></label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          value={newUserCredentials.username}
                          readOnly
                        />
                        <div className="input-group-append">
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={async () => {
                              const success = await copyToClipboard(newUserCredentials.username);
                              if (success) console.log('Username copied');
                            }}
                          >
                            <i className="fas fa-copy"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label><strong>Email</strong></label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          value={newUserCredentials.email}
                          readOnly
                        />
                        <div className="input-group-append">
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={async () => {
                              const success = await copyToClipboard(newUserCredentials.email);
                              if (success) console.log('Email copied');
                            }}
                          >
                            <i className="fas fa-copy"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label><strong>Temporary Password</strong></label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control bg-warning"
                          value={newUserCredentials.temporaryPassword}
                          readOnly
                        />
                        <div className="input-group-append">
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={async () => {
                              const success = await copyToClipboard(newUserCredentials.temporaryPassword);
                              if (success) console.log('Password copied');
                            }}
                          >
                            <i className="fas fa-copy"></i>
                          </button>
                        </div>
                      </div>
                      <small className="text-warning">
                        <i className="fas fa-exclamation-triangle mr-1"></i>
                        User must change this password on first login
                      </small>
                      <br />
                      <small className="text-danger">
                        <i className="fas fa-clock mr-1"></i>
                        <strong>Expires:</strong> {new Date(newUserCredentials.temporaryPasswordExpiresAt).toLocaleString()} 
                        ({Math.ceil((new Date(newUserCredentials.temporaryPasswordExpiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days remaining)
                      </small>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <h5><i className="fas fa-key mr-2"></i>App Client Credentials</h5>
                    <div className="form-group">
                      <label><strong>Client ID</strong></label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          value={newUserCredentials.appClientId}
                          readOnly
                        />
                        <div className="input-group-append">
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={async () => {
                              const success = await copyToClipboard(newUserCredentials.appClientId);
                              if (success) console.log('Client ID copied');
                            }}
                          >
                            <i className="fas fa-copy"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label><strong>Client Name</strong></label>
                      <input
                        type="text"
                        className="form-control"
                        value={newUserCredentials.appClientName}
                        readOnly
                      />
                    </div>

                    <div className="form-group">
                      <label><strong>Client Secret</strong></label>
                      <div className="input-group">
                        <input
                          type="password"
                          className="form-control"
                          value={newUserCredentials.clientSecret}
                          readOnly
                        />
                        <div className="input-group-append">
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={async () => {
                              const success = await copyToClipboard(newUserCredentials.clientSecret);
                              if (success) console.log('Client secret copied');
                            }}
                          >
                            <i className="fas fa-copy"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-12">
                    <div className="alert alert-info">
                      <h6><i className="fas fa-info-circle mr-2"></i>Integration Information</h6>
                      <div className="row">
                        <div className="col-md-4">
                          <small><strong>Environment:</strong> {userPoolEnvironment}</small>
                        </div>
                        <div className="col-md-4">
                          <small><strong>Created:</strong> {new Date(newUserCredentials.createdDate).toLocaleString()}</small>
                        </div>
                        <div className="col-md-4">
                          <small><strong>Password Policy:</strong> 7-day expiration</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <div className="btn-group position-relative">
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => setShowCredentialsExportDropdown(!showCredentialsExportDropdown)}
                  >
                    <i className="fas fa-download mr-2"></i>
                    Export All Credentials
                    <i className="fas fa-chevron-down ml-2"></i>
                  </button>
                  {showCredentialsExportDropdown && (
                    <div className="dropdown-menu show position-absolute" style={{ top: '100%', left: 0, zIndex: 1050 }}>
                      <button
                        className="dropdown-item"
                        onClick={() => {
                          const credentials = [{
                            Type: 'Complete User Credentials',
                            Username: newUserCredentials.username,
                            Email: newUserCredentials.email,
                            'Temporary Password': newUserCredentials.temporaryPassword,
                            'Password Expires': new Date(newUserCredentials.temporaryPasswordExpiresAt).toLocaleString(),
                            'Days Until Expiry': Math.ceil((new Date(newUserCredentials.temporaryPasswordExpiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)),
                            'Client ID': newUserCredentials.appClientId,
                            'Client Name': newUserCredentials.appClientName,
                            'Client Secret': newUserCredentials.clientSecret,
                            'Environment': userPoolEnvironment,
                            'Created': new Date(newUserCredentials.createdDate).toLocaleString()
                          }];
                          const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
                          exportToCSV(credentials, `user-credentials-${newUserCredentials.username}-${timestamp}.csv`);
                          setShowCredentialsExportDropdown(false);
                        }}
                      >
                        <i className="fas fa-file-csv mr-2"></i>
                        Export as CSV
                      </button>
                      <button
                        className="dropdown-item"
                        onClick={() => {
                          const credentials = {
                            type: 'complete-user-credentials',
                            environment: userPoolEnvironment,
                            user: {
                              username: newUserCredentials.username,
                              email: newUserCredentials.email,
                              temporaryPassword: newUserCredentials.temporaryPassword,
                              temporaryPasswordExpiresAt: newUserCredentials.temporaryPasswordExpiresAt,
                              temporaryPasswordExpiresAtFormatted: new Date(newUserCredentials.temporaryPasswordExpiresAt).toLocaleString(),
                              daysUntilExpiry: Math.ceil((new Date(newUserCredentials.temporaryPasswordExpiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                            },
                            appClient: {
                              clientId: newUserCredentials.appClientId,
                              clientName: newUserCredentials.appClientName,
                              clientSecret: newUserCredentials.clientSecret
                            },
                            createdAt: newUserCredentials.createdDate,
                            securityNotice: 'These credentials are sensitive. Store securely and do not share. User must change password on first login.',
                            passwordPolicy: {
                              temporaryPasswordValidityDays: 7,
                              note: 'Temporary password expires 7 days after user creation. User must login and change password before expiration.'
                            }
                          };
                          const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
                          exportToJSON(credentials, `user-credentials-${newUserCredentials.username}-${timestamp}.json`);
                          setShowCredentialsExportDropdown(false);
                        }}
                      >
                        <i className="fas fa-file-code mr-2"></i>
                        Export as JSON
                      </button>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCredentialsModal(false);
                    setNewUserCredentials(null);
                  }}
                >
                  <i className="fas fa-times mr-2"></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Security Warning Modal */}
      {showSecretWarningModal && selectedAppClientForSecret && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  <i className="fas fa-exclamation-triangle mr-2 text-warning"></i>
                  Security Warning
                </h4>
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    setShowSecretWarningModal(false);
                    setSelectedAppClientForSecret(null);
                  }}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="fas fa-shield-alt mr-2"></i>
                  <strong>Client secrets are highly sensitive credentials.</strong>
                </div>
                <p>
                  You are about to view the client secret for <strong>{selectedAppClientForSecret.clientName}</strong>.
                </p>
                <ul className="text-muted">
                  <li>Only view if you need to configure API access</li>
                  <li>The secret will be displayed for 60 seconds only</li>
                  <li>Store the secret securely if you copy it</li>
                  <li>Never share client secrets in unsecured channels</li>
                </ul>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowSecretWarningModal(false);
                    setSelectedAppClientForSecret(null);
                  }}
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleConfirmViewSecret}
                >
                  <i className="fas fa-eye mr-2"></i>
                  I Understand, Show Secret
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Client Secret Display Modal */}
      {showClientSecretModal && selectedAppClientForSecret && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  <i className="fas fa-key mr-2"></i>
                  App Client Credentials - {selectedAppClientForSecret.clientName}
                </h4>
                <div className="ml-auto d-flex align-items-center">
                  <span className="badge badge-warning mr-3">
                    <i className="fas fa-clock mr-1"></i>
                    Auto-close in: {secretDisplayTimeLeft}s
                  </span>
                  <button
                    type="button"
                    className="close"
                    onClick={() => {
                      setShowClientSecretModal(false);
                      setSelectedAppClientForSecret(null);
                    }}
                  >
                    <span>&times;</span>
                  </button>
                </div>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle mr-2"></i>
                  <strong>Security Notice:</strong> These credentials will not be shown again. Save them securely if needed.
                </div>
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label><strong>Client ID</strong></label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          value={selectedAppClientForSecret.clientId}
                          readOnly
                        />
                        <div className="input-group-append">
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={async () => {
                              const success = await copyToClipboard(selectedAppClientForSecret.clientId);
                              if (success) {
                                console.log('Client ID copied to clipboard');
                              }
                            }}
                          >
                            <i className="fas fa-copy"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label><strong>Client Name</strong></label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedAppClientForSecret.clientName}
                        readOnly
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label><strong>Client Secret</strong></label>
                  <div className="input-group">
                    <input
                      type={isSecretVisible ? "text" : "password"}
                      className="form-control"
                      value={selectedAppClientForSecret.clientSecret || 'Loading...'}
                      readOnly
                    />
                    <div className="input-group-append">
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setIsSecretVisible(!isSecretVisible)}
                      >
                        <i className={`fas ${isSecretVisible ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={async () => {
                          const secret = selectedAppClientForSecret.clientSecret;
                          if (secret && secret !== 'Loading...') {
                            const success = await copyToClipboard(secret);
                            if (success) {
                              console.log('Client secret copied to clipboard');
                            }
                          }
                        }}
                      >
                        <i className="fas fa-copy"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <small className="text-muted">
                      <strong>Environment:</strong> {userPoolEnvironment}
                    </small>
                  </div>
                  <div className="col-md-6">
                    <small className="text-muted">
                      <strong>Created:</strong> {new Date(selectedAppClientForSecret.creationDate).toLocaleString()}
                    </small>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <div className="btn-group position-relative">
                  <button
                    type="button"
                    className="btn btn-info"
                    onClick={() => setShowClientSecretExportDropdown(!showClientSecretExportDropdown)}
                  >
                    <i className="fas fa-download mr-2"></i>
                    Export Credentials
                    <i className="fas fa-chevron-down ml-2"></i>
                  </button>
                  {showClientSecretExportDropdown && (
                    <div className="dropdown-menu show position-absolute" style={{ top: '100%', left: 0, zIndex: 1050 }}>
                      <button
                        className="dropdown-item"
                        onClick={() => {
                          const credentials = [{
                            Type: 'App Client',
                            'Client ID': selectedAppClientForSecret.clientId,
                            'Client Name': selectedAppClientForSecret.clientName,
                            'Client Secret': selectedAppClientForSecret.clientSecret || 'sk_live_abc123def456ghi789jkl012mno345pqr678stu901vwx234yz',
                            'Environment': userPoolEnvironment,
                            'Created': selectedAppClientForSecret.creationDate,
                            'Last Modified': selectedAppClientForSecret.lastModifiedDate
                          }];
                          const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
                          exportToCSV(credentials, `app-client-credentials-${timestamp}.csv`);
                          setShowClientSecretExportDropdown(false);
                        }}
                      >
                        <i className="fas fa-file-csv mr-2"></i>
                        Export as CSV
                      </button>
                      <button
                        className="dropdown-item"
                        onClick={() => {
                          const credentials = {
                            type: 'app-client-credentials',
                            environment: userPoolEnvironment,
                            appClient: {
                              clientId: selectedAppClientForSecret.clientId,
                              clientName: selectedAppClientForSecret.clientName,
                              clientSecret: selectedAppClientForSecret.clientSecret || 'sk_live_abc123def456ghi789jkl012mno345pqr678stu901vwx234yz',
                              creationDate: selectedAppClientForSecret.creationDate,
                              lastModifiedDate: selectedAppClientForSecret.lastModifiedDate
                            },
                            exportedAt: new Date().toISOString(),
                            securityNotice: 'These credentials are sensitive. Store securely and do not share.'
                          };
                          const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
                          exportToJSON(credentials, `app-client-credentials-${timestamp}.json`);
                          setShowClientSecretExportDropdown(false);
                        }}
                      >
                        <i className="fas fa-file-code mr-2"></i>
                        Export as JSON
                      </button>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowClientSecretModal(false);
                    setSelectedAppClientForSecret(null);
                  }}
                >
                  <i className="fas fa-times mr-2"></i>
                  Close Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Group Selection Modal */}
      {showGroupSelectionModal && selectedUserForGroups && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  <i className="fas fa-users mr-2"></i>
                  Manage Groups for {selectedUserForGroups.username}
                </h4>
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    setShowGroupSelectionModal(false);
                    setSelectedUserForGroups(null);
                    setSelectedGroups([]);
                    setGroupSearchTerm(''); // Clear search term
                    setGroupFilter('all'); // Reset filter
                  }}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle mr-2"></i>
                  Select which groups this user should belong to. Changes will be applied immediately.
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <h6>Current Groups ({selectedUserForGroups.groups.length})</h6>
                    {selectedUserForGroups.groups.length > 0 ? (
                      <div className="mb-3" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                        {selectedUserForGroups.groups.map(group => (
                          <span key={group} className="badge badge-info mr-1 mb-1">
                            {group}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted">No groups assigned</p>
                    )}
                  </div>
                  <div className="col-md-6">
                    <h6>Selected Groups ({selectedGroups.length})</h6>
                    {selectedGroups.length > 0 ? (
                      <div className="mb-3" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                        {selectedGroups.map(group => (
                          <span key={group} className="badge badge-success mr-1 mb-1">
                            {group}
                            <button
                              type="button"
                              className="btn btn-sm ml-1 p-0"
                              style={{ background: 'none', border: 'none', color: 'white' }}
                              onClick={() => handleToggleGroup(group)}
                              title="Remove group"
                            >
                              <i className="fas fa-times" style={{ fontSize: '10px' }}></i>
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted">No groups selected</p>
                    )}
                  </div>
                </div>

                {/* Search and Filter Controls */}
                <div className="row mb-3">
                  <div className="col-md-8">
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">
                          <i className="fas fa-search"></i>
                        </span>
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search groups by name or description..."
                        value={groupSearchTerm}
                        onChange={(e) => setGroupSearchTerm(e.target.value)}
                      />
                      {groupSearchTerm && (
                        <div className="input-group-append">
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={() => setGroupSearchTerm('')}
                            title="Clear search"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <select
                      className="form-control"
                      value={groupFilter}
                      onChange={(e) => setGroupFilter(e.target.value as 'all' | 'selected' | 'available')}
                    >
                      <option value="all">All Groups ({allGroups.length})</option>
                      <option value="selected">Selected ({selectedGroups.length})</option>
                      <option value="available">Available ({allGroups.length - selectedGroups.length})</option>
                    </select>
                  </div>
                </div>

                {/* Bulk Actions */}
                {!allGroupsLoading && getFilteredGroups().length > 0 && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <div className="btn-group btn-group-sm" role="group">
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={handleSelectAllFilteredGroups}
                          disabled={getFilteredGroups().every(g => selectedGroups.includes(g.groupName))}
                        >
                          <i className="fas fa-check-square mr-1"></i>
                          Select All Filtered ({getFilteredGroups().length})
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={handleDeselectAllFilteredGroups}
                          disabled={!getFilteredGroups().some(g => selectedGroups.includes(g.groupName))}
                        >
                          <i className="fas fa-square mr-1"></i>
                          Deselect All Filtered
                        </button>
                        {(groupSearchTerm || groupFilter !== 'all') && (
                          <button
                            type="button"
                            className="btn btn-outline-info"
                            onClick={clearGroupFilters}
                          >
                            <i className="fas fa-filter mr-1"></i>
                            Clear Filters
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label><strong>Group Selection</strong></label>
                  <div className="border rounded p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {allGroupsLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="sr-only">Loading groups...</span>
                        </div>
                        <p className="text-muted mt-2">Loading all groups...</p>
                      </div>
                    ) : getFilteredGroups().length > 0 ? (
                      <>
                        <div className="mb-2">
                          <small className="text-muted">
                            Showing {getFilteredGroups().length} of {allGroups.length} groups
                            {groupSearchTerm && ` matching "${groupSearchTerm}"`}
                          </small>
                        </div>
                        {getFilteredGroups().map(group => (
                          <div key={group.groupName} className="form-check mb-2 p-2 border-bottom">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`group-${group.groupName}`}
                              checked={selectedGroups.includes(group.groupName)}
                              onChange={() => handleToggleGroup(group.groupName)}
                            />
                            <label className="form-check-label w-100" htmlFor={`group-${group.groupName}`}>
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                  <strong>{group.groupName}</strong>
                                  {group.description && (
                                    <small className="text-muted d-block">{group.description}</small>
                                  )}
                                </div>
                                <div className="text-right">
                                  <small className="text-muted d-block">
                                    {new Date(group.creationDate).toLocaleDateString()}
                                  </small>
                                </div>
                              </div>
                            </label>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className="text-center py-4">
                        {groupSearchTerm || groupFilter !== 'all' ? (
                          <>
                            <i className="fas fa-search fa-2x text-muted mb-2"></i>
                            <h6>No groups found</h6>
                            <p className="text-muted">
                              No groups match your current search or filter criteria.
                            </p>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={clearGroupFilters}
                            >
                              <i className="fas fa-filter mr-1"></i>
                              Clear Filters
                            </button>
                          </>
                        ) : (
                          <>
                            <i className="fas fa-users fa-2x text-muted mb-2"></i>
                            <h6>No groups available</h6>
                            <p className="text-muted">No groups are currently available for assignment.</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  <strong>Note:</strong> Group assignments will be updated immediately when you click "Save Changes".
                  {selectedGroups.length > 0 && (
                    <div className="mt-2">
                      <strong>Changes to apply:</strong>
                      <ul className="mb-0 mt-1">
                        {selectedGroups.filter(g => !selectedUserForGroups.groups.includes(g)).length > 0 && (
                          <li>
                            <strong>Add to groups:</strong> {selectedGroups.filter(g => !selectedUserForGroups.groups.includes(g)).join(', ')}
                          </li>
                        )}
                        {selectedUserForGroups.groups.filter(g => !selectedGroups.includes(g)).length > 0 && (
                          <li>
                            <strong>Remove from groups:</strong> {selectedUserForGroups.groups.filter(g => !selectedGroups.includes(g)).join(', ')}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowGroupSelectionModal(false);
                    setSelectedUserForGroups(null);
                    setSelectedGroups([]);
                    setGroupSearchTerm(''); // Clear search term
                    setGroupFilter('all'); // Reset filter
                  }}
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveUserGroups}
                >
                  <i className="fas fa-save mr-2"></i>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedUserForPasswordReset && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">
                  <i className="fas fa-key mr-2"></i>
                  Reset Password for {selectedUserForPasswordReset.username}
                </h4>
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setSelectedUserForPasswordReset(null);
                  }}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  <strong>Security Notice:</strong> You are about to reset the password for user <strong>{selectedUserForPasswordReset.username}</strong>. 
                  This action cannot be undone.
                </div>
                
                <form id="reset-password-form" onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target as HTMLFormElement);
                  const data = {
                    newPassword: formData.get('newPassword') as string,
                    temporaryPassword: formData.get('temporaryPassword') === 'on'
                  };
                  
                  if (data.newPassword.length < 8) {
                    handleApiError(new Error('Password must be at least 8 characters long'), 'IAM Management');
                    return;
                  }
                  
                  handleResetPassword(data);
                }}>
                  <div className="form-group">
                    <label htmlFor="newPassword">
                      <strong>New Password</strong>
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="newPassword"
                      name="newPassword"
                      placeholder="Enter new password (minimum 8 characters)"
                      required
                      minLength={8}
                    />
                    <small className="form-text text-muted">
                      Password must be at least 8 characters long and meet your organization's password policy.
                    </small>
                  </div>
                  
                  <div className="form-group">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="temporaryPassword"
                        name="temporaryPassword"
                        defaultChecked={true}
                      />
                      <label className="form-check-label" htmlFor="temporaryPassword">
                        <strong>Temporary Password</strong>
                      </label>
                    </div>
                    <small className="form-text text-muted">
                      If checked, the user will be required to change this password on their next login. 
                      If unchecked, this will be their permanent password.
                    </small>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setSelectedUserForPasswordReset(null);
                  }}
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </button>
                <button
                  type="submit"
                  form="reset-password-form"
                  className="btn btn-warning"
                >
                  <i className="fas fa-key mr-2"></i>
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrops */}
      {(showCreateAppClientModal || showEditAppClientModal || showCreateGroupModal || showCreateUserModal || showUsersModal || showUserDetailsModal || showDeleteUserModal || showDeleteAppClientModal || showDeleteGroupModal || showCredentialsModal || showSecretWarningModal || showClientSecretModal || showGroupSelectionModal || showResetPasswordModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
};

export default IamUserPoolManagement;